import React, { useEffect, useState } from "react";
import { IListing } from "../../lib/Listing";
import DatePicker from "react-datepicker";
import styled from "styled-components";
import { parseISO, add } from 'date-fns';

import "react-datepicker/dist/react-datepicker.css";

export interface IRentalCalendarProps {
  title?: string;
  className?: string;
  timezone?: string;
  listing: IListing;
  monthsShown?: number;
  displayRates?: boolean;
  displayLabels?: boolean;
  openToDate?: Date;
  minimumDate?: Date;
  maximumDate?: Date;
}

export const RentalCalendar: React.FC<IRentalCalendarProps> = ({
  title,
  listing,
  timezone,
  className,
  openToDate,
  monthsShown = 4,
  displayRates = false,
  displayLabels = false,
  minimumDate,
  maximumDate,
}) => {
  const dateTypes: any = {
    start: [],
    startOverlapping: [],
    mid: [],
    end: [],
  };

  const [showCalendar, setShowCalendar] = useState(false);

  if (listing.bookings) {

    const allEndDates = listing.bookings.map((block) => block.endDate);

    listing.bookings.forEach((block) => {      
      let startDate = parseISO(block.startDate);
      let endDate = parseISO(block.endDate);

      if (allEndDates.indexOf(block.startDate) === -1) {
        dateTypes.start.push(startDate);
      }
      else {
        dateTypes.startOverlapping.push(startDate);
      }
      dateTypes.end.push(endDate);
      startDate = add(startDate, {days: 1});
      while (startDate < endDate) {
        dateTypes.mid.push(startDate);
        startDate = add(startDate, {days: 1});
      }
    });
  }

  const highlightWithRanges = [
    { "custom-start": dateTypes.start },
    { "custom-start-overlapping": dateTypes.startOverlapping },
    { "custom-mid": dateTypes.mid },
    { "custom-end": dateTypes.end },
  ];

  const minDate = minimumDate ?? new Date();
  const maxDate = maximumDate ?? new Date().setFullYear(new Date().getFullYear() + 1);

  let optionalProps: any = {};
  if (displayRates) {
    const ratesMinDate = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate())
    const activeRates = listing.rates?.filter(rate => {
      return (rate.endDate && new Date(rate.endDate) >= ratesMinDate)
    })

    const calendarRates: any = {};
    activeRates?.forEach(item => {
      let stepDate = item.startDate && new Date(item.startDate)
      const endDate = item.endDate && new Date(item.endDate)
      if (stepDate instanceof Date && endDate instanceof Date) {
        while (stepDate <= endDate) {
          if (stepDate.getDay() == 0) {
            calendarRates[stepDate.toDateString()] = { price: item.price, type: item.type }
          }
          stepDate.setDate(stepDate.getDate() + 1)
        }
      }
    })

    const renderDayContents = (day: number, date: Date) => {
      if (calendarRates[date.toDateString()]) {
        const item = calendarRates[date.toDateString()]
        return (
          <>
            {day}
            <span className="absolute bottom-0 left-1 right-1 text-body-text font-semibold text-base z-10 price">
              {item.price && `${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0, minimumFractionDigits: 0 }).format(item.price)}/${item.type ?? "week"}`}
            </span>
          </>)
      }
      return day
    };

    optionalProps = {
      renderDayContents: renderDayContents
    }
  }

  useEffect(() => {
    setShowCalendar(true)
  }, [])

  const Calendar = styled.div`
    .react-datepicker{
      @media (max-width: 666px) {
        display:flex;
        flex-direction: column;
        align-items:center;
      }
    }
    .react-datepicker__navigation{
      top:13px;
      span:before{
        border-color: var(--color-body-text)
      }
    }
    } .react-datepicker__current-month {
      font-weight: 400;
      padding: 0.25rem 0 0.75rem 0;
      font-style: italic;
    }
    .react-datepicker__week {
      display: flex;
      flex-direction: row;
      align-items: stretch;
      border: 1px solid #dddddd;
      border-top: none;    
      position: relative;  
    }
    .react-datepicker__week:nth-child(1) {
      border-top: 1px solid #dddddd;      
    }
    .react-datepicker__day{
          
      margin: 0;
      pointer-events: none;
      border-left: 1px solid #dddddd;
    }
    .react-datepicker__day > span{
      text-shadow: #fff 1px 0 10px;
    }
    .react-datepicker__day,
    .react-datepicker__day-name{
      height: 2.7rem;
      width: 2.7rem;      
    }
    .react-datepicker__day:nth-child(1){
      border-left: none;
    }
    .react-datepicker__day-names {
      background: var(--color-body-bg-inverted);
    }
    .react-datepicker__day-name {
      color: var(--color-body-text-inverted);
    }
    .react-datepicker__day-name {
      margin: 1px 0;
      pointer-events: none;
    }
    .custom-mid {
      color: var(--color-action-text);
      background: var(--color-action);
      cursor: not-allowed;
    }
    .custom-start {
      background: linear-gradient(
          225deg,
          var(--color-action) 25%,
          transparent 25%
        ),
        linear-gradient(315deg, var(--color-action) 25%, transparent 25%);
    }
    .custom-end {
      background: linear-gradient(
          135deg,
          var(--color-action) 25%,
          transparent 25%
        ),
        linear-gradient(45deg, var(--color-action) 25%, transparent 25%);
    }
    .custom-start-overlapping.custom-end {
      background: linear-gradient(
          135deg,
          var(--color-action) 25%,
          transparent 25% 
        ),
        linear-gradient(
          45deg,
          var(--color-action) 25%,
          transparent 25%
        ),
        linear-gradient(
          225deg,
          var(--color-action) 25%,
          transparent 25%
        ),
        linear-gradient(
          315deg,
          var(--color-action) 25%,
          transparent 25%
        );
    }
  `;


  return (
    <>
      {title && <h2 className="block text-h2-text">{title}</h2>}
      <Calendar className={`rental-calendar ${className}`}>
        {showCalendar && (<DatePicker
          className="flex flex-col items-center sm:inline-block"
          minDate={minDate}
          maxDate={maxDate}
          highlightDates={highlightWithRanges}
          selected={false}
          monthsShown={monthsShown}
          disabledKeyboardNavigation
          inline
          readOnly
          openToDate={openToDate}
          {...optionalProps}
        />)}
        {displayLabels && (
          <div className="flex flex-row flex-wrap justify-start py-2 gap-2">
            <div className="w-full md:w-auto flex flex-row flex-nowrap items-center gap-1">
              <div className="border border-gray-400 w-4 h-4"></div>
              <span className="uppercase">Available</span>
            </div>
            <div className="w-full md:w-auto flex flex-row flex-nowrap items-center gap-1">
              <div className="border border-gray-400 w-4 h-4 bg-action"></div>
              <span className="uppercase">Not Available</span>
            </div>
          </div>
        )}
      </Calendar>
    </>
  );
};
